"use client"

import type { GalleryItem } from "@/modules/companies/client/api/types"
import type { FunctionComponent } from "react"

import classNames from "classnames"
import dynamic from "next/dynamic"
import Image from "next/image"
import pDebounce from "p-debounce"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { ChevronLeft } from "@jobteaser/spark/components/Icons/ChevronLeft"
import { ChevronRight } from "@jobteaser/spark/components/Icons/ChevronRight"

import { YOUTUBE_URL_REGEX } from "@/modules/companies/client/components/Media/constants"
import youtubePlayIcon from "@/modules/companies/client/components/Media/images/youtube_play_icon.png"
import { useJobAdDetailTracking } from "@/modules/jobAdDetail/hooks/useJobAdDetailTracking"

import styles from "./Gallery.module.css"

const FullScreenGalleryDynamic = dynamic(() => import("./FullScreenGallery").then(mod => mod.FullScreenGallery), {
  ssr: false,
})

const DEBOUNCED_SCROLL_DELAY = 25

type GalleryProps = {
  fullscreenCloseLabel: string
  items: GalleryItem<"MEDIUM_TYPE_FOR_JOB_DETAIL_VIDEO" | "MEDIUM_TYPE_FOR_JOB_DETAIL_IMAGE">[]
  nextLabel: string
  previousLabel: string
}

export const Gallery: FunctionComponent<GalleryProps> = ({ fullscreenCloseLabel, items, nextLabel, previousLabel }) => {
  const { trackJobAdDetailGalleryMediaItem, trackEventClick } = useJobAdDetailTracking()

  const mediaLength = items?.length || 0

  const [showLeftButton, setLeftButton] = useState(false)
  const [showRightButton, setRightButton] = useState(mediaLength > 1)
  const [previewUrlToZoomIn, setPreviewUrlToZoomIn] = useState<string | null>(null)

  const carouselRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    if (!carouselRef.current) {
      return
    }

    if (carouselRef.current.scrollLeft > 0) {
      setLeftButton(true)
    } else {
      setLeftButton(false)
    }

    if (carouselRef.current.scrollWidth - carouselRef.current.scrollLeft - carouselRef.current.offsetWidth > 0) {
      setRightButton(true)
    } else {
      setRightButton(false)
    }
  }, [carouselRef])

  const debouncedScroll = useMemo(() => pDebounce(handleScroll, DEBOUNCED_SCROLL_DELAY), [handleScroll])

  useEffect(() => {
    const currentCarouselRef = carouselRef.current

    if (currentCarouselRef) {
      currentCarouselRef.addEventListener("scroll", debouncedScroll)
    }
    return () => {
      if (currentCarouselRef) {
        currentCarouselRef.removeEventListener("scroll", debouncedScroll)
      }
    }
  }, [debouncedScroll])

  function scroll(direction: "left" | "right"): void {
    if (!carouselRef.current) {
      return
    }

    // 180 is a default but it should never happen since it's not an orphan element
    const parentWith = carouselRef.current.parentElement?.offsetWidth || 180

    carouselRef.current.scrollBy({ behavior: "smooth", left: direction === "left" ? -1 * parentWith : parentWith })

    trackEventClick(`job-ad-detail-company-carousel-${direction}-button-click`)
  }

  function handleClick({
    previewUrl,
  }: GalleryItem<"MEDIUM_TYPE_FOR_JOB_DETAIL_IMAGE" | "MEDIUM_TYPE_FOR_JOB_DETAIL_VIDEO">): void {
    setPreviewUrlToZoomIn(previewUrl)
  }

  function handleModalClose(): void {
    setPreviewUrlToZoomIn(null)
    trackEventClick("job-ad-detail-company-carousel-close-fullscreen-click")
  }

  if (mediaLength === 0) {
    return null
  }

  const wrapperStyles = classNames(styles.wrapper, mediaLength === 1 && styles.wrapper___withOneItem)

  return (
    <div className={wrapperStyles}>
      <div className={styles.main} ref={carouselRef}>
        {items.map(item => {
          const { previewUrl, type, url, description } = item

          const isVideo = type === "MEDIUM_TYPE_FOR_JOB_DETAIL_VIDEO" && YOUTUBE_URL_REGEX.test(url)

          return (
            <div
              aria-label={description}
              key={previewUrl}
              className={classNames(styles.galleryItem, {
                [styles.galleryItem__video]: isVideo,
                [styles.galleryItem__img]: !isVideo,
              })}
              role="button"
              tabIndex={0}
              onClick={() => {
                handleClick(item)
                trackJobAdDetailGalleryMediaItem(isVideo)
              }}
              onKeyDown={() => {
                handleClick(item)
              }}
            >
              <Image
                className={classNames(styles.previewImage, {
                  [styles.previewImage__video]: isVideo,
                  [styles.previewImage__img]: !isVideo,
                })}
                src={previewUrl}
                height={400}
                width={400}
                alt={description}
              />
              {isVideo && <Image alt="" src={youtubePlayIcon} height={48} width={68} className={styles.youtubeIcon} />}
            </div>
          )
        })}
      </div>
      {showLeftButton && (
        <IconButton
          aria-label={previousLabel}
          className={classNames(styles.navigationButton, styles.navigationButton___left)}
          icon={ChevronLeft}
          onBackground="dark"
          onClick={() => {
            scroll("left")
          }}
          variant="filled"
        />
      )}
      {showRightButton && (
        <IconButton
          aria-label={nextLabel}
          className={classNames(styles.navigationButton, styles.navigationButton___right)}
          icon={ChevronRight}
          onBackground="dark"
          onClick={() => {
            scroll("right")
          }}
          variant="filled"
        />
      )}
      {previewUrlToZoomIn && (
        <FullScreenGalleryDynamic
          fullscreenCloseLabel={fullscreenCloseLabel}
          onClose={() => handleModalClose()}
          items={items}
          currentPreviewUrl={previewUrlToZoomIn}
        />
      )}
    </div>
  )
}
